import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import type { ApiEnvelope, PageMeta } from '@parkline/shared';

/** Ключи localStorage. Один источник правды для api.ts и auth.tsx. */
export const TOKEN_KEY = 'parkline.token';
export const USER_KEY = 'parkline.user';

export const LOGIN_PATH = '/login';
const LOGIN_ENDPOINT = '/auth/login';
const HTTP_UNAUTHORIZED = 401;
const DEFAULT_ERROR = 'Сервис недоступен. Повторите попытку позже.';
const DEFAULT_LIMIT = 20;

/** Тело ответа после разворачивания конверта. */
export interface Unwrapped<T> {
  data: T;
  meta?: PageMeta;
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

export function readToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/** Один префикс и для axios, и для скачивания файлов. */
const API_PREFIX = '/api/v1';

export const http = axios.create({
  baseURL: API_PREFIX,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = readToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/**
 * Ответ бэкенда всегда обёрнут в ApiEnvelope. Разворачиваем его здесь, чтобы
 * страницы работали с чистыми данными и никогда не проверяли success руками.
 */
http.interceptors.response.use(
  (response: AxiosResponse<unknown>) => {
    const envelope = response.data as ApiEnvelope<unknown> | null;
    if (envelope && typeof envelope === 'object' && 'success' in envelope) {
      if (!envelope.success) {
        throw new Error(envelope.error ?? DEFAULT_ERROR);
      }
      return { ...response, data: { data: envelope.data, meta: envelope.meta } };
    }
    // Ответ без конверта (например, статика или прокси-ошибка) — пробрасываем как есть.
    return { ...response, data: { data: response.data } };
  },
  (error: AxiosError<ApiEnvelope<unknown>>) => {
    if (error.response?.status === HTTP_UNAUTHORIZED) {
      handleUnauthorized(error.config?.url);
    }
    return Promise.reject(new Error(extractMessage(error)));
  },
);

function extractMessage(error: AxiosError<ApiEnvelope<unknown>>): string {
  const fromEnvelope = error.response?.data?.error;
  if (fromEnvelope) {
    return fromEnvelope;
  }
  return error.message || DEFAULT_ERROR;
}

/**
 * Истёкший токен: чистим сессию и уводим на логин через window.location —
 * интерцептор живёт вне дерева React, хуки роутера здесь недоступны.
 */
function handleUnauthorized(url?: string): void {
  if (url?.includes(LOGIN_ENDPOINT)) {
    return; // неверный логин/пароль — это не истёкшая сессия
  }
  clearSession();
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
}

/** Пустые значения в query не отправляем — бэкенд с forbidNonWhitelisted строг. */
function cleanParams(params?: QueryParams): QueryParams | undefined {
  if (!params) {
    return undefined;
  }
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/**
 * Скачивание файла с бэкенда.
 *
 * Мимо axios намеренно: перехватчик ответов разворачивает конверт `{success, data}` и
 * подменяет `response.data` — до создания ссылки доходил объект `{ data: Blob }`, а не
 * сам файл, и скачивание молча не происходило. Обычная ссылка `<a href>` тоже не годится:
 * эндпоинт закрыт токеном, а браузер его в запрос за файлом не добавит.
 *
 * Имя файла берём из `Content-Disposition` (там кириллица в кодировке RFC 5987), а если
 * сервер его не назвал — из аргумента.
 */
export async function download(url: string, fallbackName: string): Promise<void> {
  const token = readToken();
  const response = await fetch(`${API_PREFIX}${url}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(await downloadError(response));
  }

  const blob = await response.blob();
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(
    response.headers.get('content-disposition') ?? '',
  )?.[1];

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = encoded ? decodeURIComponent(encoded) : fallbackName;
  document.body.append(link);
  link.click();
  link.remove();
  // Освобождаем не сразу: Safari успевает начать загрузку только после кадра.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/** Ошибку сервер отдаёт обычным конвертом — показываем её текст, а не код состояния. */
async function downloadError(response: Response): Promise<string> {
  try {
    const envelope = (await response.json()) as { error?: string } | null;
    if (envelope?.error) {
      return envelope.error;
    }
  } catch {
    // Тело не JSON (например, ошибка прокси) — обойдёмся кодом состояния.
  }
  return `Не удалось скачать файл: ответ сервера ${response.status}`;
}

export async function get<T>(url: string, params?: QueryParams): Promise<T> {
  const response = await http.get<Unwrapped<T>>(url, { params: cleanParams(params) });
  return response.data.data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.post<Unwrapped<T>>(url, body ?? {});
  return response.data.data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.patch<Unwrapped<T>>(url, body ?? {});
  return response.data.data;
}

export async function del<T>(url: string, params?: QueryParams): Promise<T> {
  const response = await http.delete<Unwrapped<T>>(url, { params: cleanParams(params) });
  return response.data.data;
}

/**
 * Страничная выборка: бэкенд отдаёт массив в data, а total/page/limit — в meta.
 */
export async function getPaged<T>(
  url: string,
  params?: QueryParams,
): Promise<{ items: T[]; meta: PageMeta }> {
  const response = await http.get<Unwrapped<T[]>>(url, { params: cleanParams(params) });
  const items = response.data.data ?? [];
  const meta = response.data.meta ?? {
    total: items.length,
    page: 1,
    limit: items.length || DEFAULT_LIMIT,
  };
  return { items, meta };
}
